<script lang="ts">
    import { onDestroy, onMount } from "svelte";
    // import { createPopperActions } from "svelte-popperjs";
    import type { LocalizedString } from "typesafe-i18n";
    import { LL } from "../../../i18n/i18n-svelte";
    import { gameManager } from "../../Phaser/Game/GameManager";
    import { EditorToolName } from "../../Phaser/Game/MapEditor/MapEditorModeManager";
    import {
        mapEditorSelectedToolStore,
        mapEditorToolsWidth,
        mapEditorVisibilityStore,
    } from "../../Stores/MapEditorStore";
    import { analyticsClient } from "../../Administration/AnalyticsClient";
    import { mapEditorActivated, mapEditorActivatedForThematics } from "../../Stores/MenuStore";
    import { isMediaBreakpointUp } from "../../Utils/BreakpointsUtils";
    import ArrowBarRight from "../Icons/ArrowBarRight.svelte";
    import CloseIcon from "../Icons/meta/CloseIcon.svelte";
    import type { WorkAdventureComponent } from "../../../types/component";
    import { IconTexture, IconLamp, IconMapSearch, IconSettings, IconTrash } from "@wa-icons";

    type SideBarTool = {
        toolName: EditorToolName;
        iconComponent: WorkAdventureComponent;
        tooltiptext: LocalizedString;
    };

    const direction = document.documentElement.getAttribute("dir") || "ltr";

    const exploreTheRoomTool = {
        toolName: EditorToolName.ExploreTheRoom,
        iconComponent: IconMapSearch,
        tooltiptext: $LL.mapEditor.sideBar.exploreTheRoom(),
    };

    const entityEditorTool = {
        toolName: EditorToolName.EntityEditor,
        iconComponent: IconLamp,
        tooltiptext: $LL.mapEditor.sideBar.entityEditor(),
    };
    const trashEditorTool = {
        toolName: EditorToolName.TrashEditor,
        iconComponent: IconTrash,
        tooltiptext: $LL.mapEditor.sideBar.trashEditor(),
    };

    let availableTools = $derived.by<SideBarTool[]>(() => {
        const tools: SideBarTool[] = [exploreTheRoomTool];

        if ($mapEditorActivatedForThematics && !$mapEditorActivated) {
            tools.push(entityEditorTool, trashEditorTool);
        }

        if ($mapEditorActivated && !isMobile) {
            tools.push({
                toolName: EditorToolName.AreaEditor,
                iconComponent: IconTexture,
                tooltiptext: $LL.mapEditor.sideBar.areaEditor(),
            });
            tools.push(entityEditorTool);
            tools.push({
                toolName: EditorToolName.WAMSettingsEditor,
                iconComponent: IconSettings,
                tooltiptext: $LL.mapEditor.sideBar.configureMyRoom(),
            });
            tools.push(trashEditorTool);
        }

        return tools;
    });

    function switchTool(newTool: EditorToolName) {
        // The map sidebar is opened when the user clicks on the explorer for the first time.
        // If the user clicks on the Explorer again, we need to show the map sidebar.
        if (newTool === EditorToolName.ExploreTheRoom && $mapEditorSelectedToolStore === newTool) {
            mapEditorVisibilityStore.set(!$mapEditorVisibilityStore);
        } else {
            mapEditorVisibilityStore.set(true);
        }
        analyticsClient.trackAdminEvent("map_editor.tool.opened", { name: newTool });
        gameManager.getCurrentGameScene().getMapEditorModeManager()?.equipTool(newTool);
    }

    function toggleMapEditor() {
        mapEditorVisibilityStore.set(!$mapEditorVisibilityStore);
    }

    let sectionSideBarContainer: HTMLElement;
    let isMobile = $state(isMediaBreakpointUp("md"));
    const resizeObserver = new ResizeObserver(() => {
        isMobile = isMediaBreakpointUp("md");
    });

    onMount(() => {
        resizeObserver.observe(sectionSideBarContainer);
    });

    onDestroy(() => {
        resizeObserver.unobserve(sectionSideBarContainer);
    });
</script>

<section
    bind:this={sectionSideBarContainer}
    class="side-bar-container z-1999 pointer-events-auto bg-mlight/80 h-full backdrop-blur-md"
    class:!right-20={!$mapEditorVisibilityStore}
    style="width: {mapEditorToolsWidth}px;"
>
    <!--put a section to avoid lower div to be affected by some css-->
    <div class="flex flex-col items-center gap-6 pt-6 side-bar">
        <div class="flex flex-col gap-3 items-center">
            <button
                class="btn w-12 h-12 p-0! rounded-full! bg-mred shadow-red hover:bg-mred-darker"
                data-testid="closeMapEditorButton"
                onclick={(event) => {
                    event.preventDefault();
                    switchTool(EditorToolName.CloseMapEditor);
                }}
                type="button"
                aria-label="Закрыть"
            >
                <CloseIcon width="w-6" height="h-6" strokeColor="stroke-white" fillColor="fill-white" />
            </button>
            <button
                class="btn btn-light rounded-full! aspect-square w-12 p-0!"
                data-testid="hideMapEditorButton"
                onclick={(event) => {
                    event.preventDefault();
                    toggleMapEditor();
                }}
                type="button"
                aria-label="Свернуть"
            >
                <ArrowBarRight
                    height="h-5"
                    width="w-5"
                    strokeColor="stroke-mdark"
                    fillColor="fill-transparent"
                    classList={`aspect-ratio transition-all ${direction === "rtl" ? "rotate-180" : ""} ${
                        $mapEditorVisibilityStore ? "" : "rotate-180"
                    }`}
                />
            </button>
        </div>
        <div class="flex flex-col gap-3">
            {#each availableTools as tool (tool.toolName)}
                {@const ToolIcon = tool.iconComponent}
                <div class="tool-button relative">
                    <button
                        class="btn btn-light peer aspect-square w-12 rounded-full! p-0! {$mapEditorSelectedToolStore ===
                        tool.toolName
                            ? 'bg-mblue!'
                            : ''}"
                        id={tool.toolName}
                        class:active={$mapEditorSelectedToolStore === tool.toolName}
                        onclick={(event) => {
                            event.preventDefault();
                            switchTool(tool.toolName);
                        }}
                        type="button"
                        aria-label={tool.tooltiptext}
                        aria-pressed={$mapEditorSelectedToolStore === tool.toolName}
                    >
                        <ToolIcon
                            font-size="22"
                            class={$mapEditorSelectedToolStore === tool.toolName && "text-white"}
                        />
                    </button>
                    <div
                        class="bg-mlight/80 backdrop-blur-xl tooltip absolute text-nowrap p-2 invisible opacity-0 transition-all peer-hover:visible peer-hover:opacity-100 rounded top-1/2 -translate-y-1/2 right-[150%]"
                    >
                        {tool.tooltiptext}
                    </div>
                </div>
            {/each}
        </div>
    </div>
</section>
